import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Package,
  Menu,
  LogOut,
  User,
  ChevronRight,
  Truck,
  Clock,
  AlertCircle,
  Settings,
  MessageCircle,
  BarChart3,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";

const SideBar = ({ isOpen, setIsOpen, setIsChatOpen, unreadCount, returnedCount }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const menuItems = [
    { key: "dashboard", icon: LayoutDashboard, path: "/" },
    { key: "orders", icon: ShoppingCart, path: "/orders" },
    {
      key: "products",
      icon: Package,
      path: "/products",
      roles: ["admin", "gerant"],
    },
    {
      key: "delivery",
      icon: Truck,
      path: "/delivery-companies",
      roles: ["admin"],
    },
    {
      key: "shipping_queue",
      icon: Truck,
      path: "/shipping-queue",
      roles: ["admin", "confirmation"],
    },
    {
      key: "tracking_queue",
      icon: Clock,
      path: "/delivery-tracking",
      roles: ["admin", "confirmation", "gerant"],
    },
    {
      key: "unassigned_queue",
      icon: AlertCircle,
      path: "/unassigned-queue",
      roles: ["admin"],
    },
    { key: "payroll", icon: Clock, path: "/payroll", roles: ["admin"] },
    {
      key: "stock",
      icon: BarChart3,
      path: "/stock",
      roles: ["admin", "gerant"],
    },
    { key: "users", icon: Users, path: "/users", roles: ["admin"] },
    // { key: "chat", icon: MessageCircle, action: () => setIsChatOpen(true) },
    {
      key: "settings",
      icon: Settings,
      path: "/settings",
      roles: ["admin", "confirmation"],
    },

    {
      key: "brands",
      icon: LayoutDashboard,
      path: "/brands",
      roles: ["superadmin"],
    },
  ];

  const filteredMenuItems = menuItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role)),
  );

  return (
    <div
      className={`
            h-screen w-64 bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0 z-[60]
            transition-transform duration-300 ease-in-out md:translate-x-0
            ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
    >
      {/* Header */}
      <div className="px-4 py-4 flex-shrink-0 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-3 px-1">
          <div className="w-8 h-8 bg-gradient-to-tr from-indigo-600 to-blue-500 rounded-lg flex items-center justify-center shadow-sm shadow-blue-200">
            <Menu className="text-white w-4 h-4" />
          </div>
          <span className="text-lg font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
            Adminify
          </span>
        </div>
        {/* Mobile Close Button */}
        <button
          onClick={() => setIsOpen(false)}
          className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-400 rotate-180" />
        </button>
      </div>

      {/* Scrollable Nav Area */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-2">
          {t("sidebar.menu")}
        </div>
        <div className="space-y-0.5">
          {filteredMenuItems.map((item) =>
            item.path ? (
              <NavLink
                key={item.key}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => `
                  flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group
                  ${
                    isActive
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {item.key === "stock" && returnedCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white animate-pulse" />
                    )}
                  </div>
                  <span className="text-[15px] font-medium leading-none">
                    {t(`sidebar.${item.key}`)}
                    {item.key === "stock" && returnedCount > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-rose-100 text-rose-600 rounded-md">
                        {returnedCount}
                      </span>
                    )}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </NavLink>
            ) : (
              <button
                key={item.key}
                onClick={() => {
                  item.action();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {item.key === "chat" && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                    )}
                  </div>
                  <span className="text-[15px] font-medium leading-none">
                    {t(`sidebar.${item.key}`)}
                    {item.key === "chat" && unreadCount > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-600 rounded-md">
                        {unreadCount}
                      </span>
                    )}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ),
          )}
        </div>
      </nav>

      {/* Fixed Bottom Language & User Section */}
      <div className="border-t border-gray-100 flex-shrink-0 bg-white/80 py-2">
        <div className="px-4 mb-2">
          <LanguageSwitcher />
        </div>
        <div className="px-3 pb-2">
          {user && (
            <div className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors group">
              <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200 flex-shrink-0">
                <User className="w-4 h-4 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user.name}
                </p>
                <p className="text-[11px] text-gray-400 truncate capitalize">
                  {user.role}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors text-gray-400 flex-shrink-0"
                title={t("sidebar.logout")}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideBar;
